import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, X, Play, FileText, Download } from 'lucide-react';
import { apiClient, API } from '../../services/api';
import { toast } from '../../hooks/use-toast';
const payroll = {
  runs: () => apiClient.get('/payroll/runs').then((r) => r.data),
  createRun: (p) => apiClient.post('/payroll/runs', p).then((r) => r.data),
  execute: (id) => apiClient.post(`/payroll/runs/${id}/execute`).then((r) => r.data),
  handoff: (id) => apiClient.post(`/payroll/runs/${id}/handoff`).then((r) => r.data),
  markPosted: (id) => apiClient.post(`/payroll/runs/${id}/mark-posted`).then((r) => r.data),
  runPayslips: (id) => apiClient.get(`/payroll/runs/${id}/payslips`).then((r) => r.data),
  minePayslips: () => apiClient.get('/payroll/payslips/mine').then((r) => r.data),
  payslip: (id) => apiClient.get(`/payroll/payslips/${id}`).then((r) => r.data),
  loans: () => apiClient.get('/payroll/loans').then((r) => r.data),
  createLoan: (p) => apiClient.post('/payroll/loans', p).then((r) => r.data),
  reimbs: () => apiClient.get('/payroll/reimbursements').then((r) => r.data),
  createReimb: (p) => apiClient.post('/payroll/reimbursements', p).then((r) => r.data),
  decideReimb: (id, p) => apiClient.patch(`/payroll/reimbursements/${id}`, p).then((r) => r.data),
  loanTypes: () => apiClient.get('/hr/masters/loan-types').then((r) => r.data),
  reimbCats: () => apiClient.get('/hr/masters/reimb-categories').then((r) => r.data),
  employees: () => apiClient.get('/hr/employees').then((r) => r.data),
};

const openPayslipHtml = (id) => {
  const token = localStorage.getItem('token') || '';
  // Open in new tab via fetch to attach auth header (since <a> can't)
  fetch(`${API}/payroll/payslips/${id}/html`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => r.text())
    .then((html) => {
      const w = window.open('', '_blank');
      w.document.write(html);
      w.document.close();
    });
};

const PayrollPage = () => {
  const [tab, setTab] = useState('runs');
  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="mb-4 sm:mb-6">
        <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">Payroll</span>
        <h1 className="mt-2 text-3xl sm:text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>
          Monthly runs & payslips
        </h1>
        <p className="mt-1 text-xs sm:text-sm text-[#0b1a2e]/60">Configure runs, execute, and review payslips.</p>
      </div>
      <div className="flex border-b border-black/5 bg-[#f7fbf8] rounded-t-2xl overflow-x-auto mb-4">
        {[['runs', 'Payroll runs'], ['mine', 'My payslips'], ['loans', 'Loans'], ['reimb', 'Reimbursements']].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`payroll-tab-${k}`}
            className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap ${tab === k ? 'text-[#0f5c3d] bg-white border-b-2 border-[#0f5c3d]' : 'text-[#0b1a2e]/60 hover:text-[#0b1a2e]'}`}>
            {label}
          </button>
        ))}
      </div>
      {tab === 'runs' && <RunsTab />}
      {tab === 'mine' && <MyPayslipsTab />}
      {tab === 'loans' && <LoansTab />}
      {tab === 'reimb' && <ReimbTab />}
    </div>
  );
};

const RunsTab = () => {
  const [runs, setRuns] = useState([]);
  const [selected, setSelected] = useState(null);
  const [payslips, setPayslips] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const load = useCallback(async () => { const r = await payroll.runs(); setRuns(r.data || []); }, []);
  useEffect(() => { load(); }, [load]);
  const openRun = async (r) => {
    setSelected(r);
    const p = await payroll.runPayslips(r.id);
    setPayslips(p.data || []);
  };
  const execute = async (r) => {
    if (!window.confirm(`Execute payroll for ${r.label}? This will (re)generate payslips for all matching employees.`)) return;
    try {
      const res = await payroll.execute(r.id);
      toast({ title: 'Executed', description: `${res.payslips} payslips · Net ${res.net.toFixed(2)}` });
      await load();
      await openRun({ ...r, id: r.id });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Attendance approval gate
      if (typeof detail === 'object' && detail?.code === 'attendance_not_approved') {
        const nudge = detail.nudge_user;
        const msg = detail.message + (nudge ? `\n\nNudge: ${nudge.name} (${nudge.email})` : '') + '\n\nOverride and run anyway?';
        if (window.confirm(msg)) {
          try {
            const res = await apiClient.post(`/payroll/runs/${r.id}/execute?force=true`).then((x) => x.data);
            toast({ title: 'Executed (override)', description: `${res.payslips} payslips` });
            await load(); await openRun({ ...r, id: r.id });
          } catch (e2) { toast({ title: e2?.response?.data?.detail?.message || e2?.response?.data?.detail || 'Force failed', variant: 'destructive' }); }
        }
        return;
      }
      toast({ title: detail || 'Execute failed', variant: 'destructive' });
    }
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
      <div className="lg:col-span-5 bg-white border border-black/5 rounded-2xl overflow-hidden">
        <div className="p-3 border-b border-black/5 flex justify-between">
          <div className="text-sm">{runs.length} runs</div>
          <button onClick={() => setShowNew(true)} data-testid="new-run-btn"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#0f5c3d] text-white text-xs"><Plus className="w-3.5 h-3.5" /> New run</button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto">
          {runs.map((r) => (
            <button key={r.id} onClick={() => openRun(r)} data-testid={`run-row-${r.id}`}
              className={`w-full text-left p-3 border-b border-black/5 hover:bg-[#f7fbf8] ${selected?.id === r.id ? 'bg-[#f7fbf8]' : ''}`}>
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium">{r.label}</div>
                <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${r.status === 'executed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{r.status}</span>
              </div>
              <div className="text-[11px] text-[#0b1a2e]/50 mt-0.5">{r.stats?.employees || 0} employees · Net {r.stats?.net?.toFixed(2) || '0.00'}</div>
            </button>
          ))}
          {runs.length === 0 && <div className="p-8 text-center text-[#0b1a2e]/50 text-sm">No runs yet — create one.</div>}
        </div>
      </div>
      <div className="lg:col-span-7">
        {selected ? (
          <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
            <div className="p-4 border-b border-black/5 flex items-center justify-between">
              <div>
                <div className="text-lg font-semibold">{selected.label}</div>
                <div className="text-xs text-[#0b1a2e]/60">{selected.status === 'executed' ? `Executed · ${payslips.length} payslips` : 'Draft'}</div>
              </div>
              {selected.status !== 'executed' && (
                <button onClick={() => execute(selected)} data-testid="execute-run-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#0f5c3d] text-white text-sm"><Play className="w-3.5 h-3.5" /> Execute</button>
              )}
              {selected.status === 'executed' && !selected.handed_off && (
                <div className="flex gap-2">
                  <button onClick={() => execute(selected)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#0f5c3d]/20 text-xs">Re-run</button>
                  <button onClick={async () => {
                    if (!window.confirm(`Handoff ${selected.label} to Accounts? After handoff HR can no longer edit this run.`)) return;
                    try { await payroll.handoff(selected.id); toast({ title: 'Handed off to Accounts' }); await load(); await openRun({ ...selected, id: selected.id }); }
                    catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
                  }} data-testid="handoff-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-amber-500 text-white text-xs font-semibold">→ Handoff to Accounts</button>
                </div>
              )}
              {selected.handed_off && selected.accounts_status === 'pending_post' && (
                <div className="flex gap-2">
                  <span className="text-[10px] uppercase font-bold px-2 py-1 rounded bg-amber-100 text-amber-800">Pending accounts</span>
                  <button onClick={async () => { try { await payroll.markPosted(selected.id); toast({ title: 'Marked posted' }); await load(); await openRun({ ...selected, id: selected.id }); } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); } }}
                    data-testid="mark-posted-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-emerald-600 text-white text-xs font-semibold">✓ Mark posted to Books</button>
                </div>
              )}
              {selected.accounts_status === 'posted' && (
                <span className="text-[10px] uppercase font-bold px-2 py-1 rounded bg-emerald-100 text-emerald-800">Posted · locked</span>
              )}
            </div>
            <table className="w-full text-sm">
              <thead className="bg-[#f7fbf8]"><tr>
                <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Employee</th>
                <th className="text-right p-2 text-xs uppercase text-[#0b1a2e]/60">Gross</th>
                <th className="text-right p-2 text-xs uppercase text-[#0b1a2e]/60">Deductions</th>
                <th className="text-right p-2 text-xs uppercase text-[#0b1a2e]/60">Net</th>
                <th></th>
              </tr></thead>
              <tbody>{payslips.map((p) => (
                <tr key={p.id} className="border-t border-black/5">
                  <td className="p-2">{p.user_name}</td>
                  <td className="p-2 text-right">{p.gross.toFixed(2)} {p.currency}</td>
                  <td className="p-2 text-right">{p.deductions_total.toFixed(2)}</td>
                  <td className="p-2 text-right font-bold text-[#0f5c3d]">{p.net.toFixed(2)}</td>
                  <td className="p-2 text-right">
                    <button onClick={() => openPayslipHtml(p.id)} data-testid={`view-payslip-${p.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-[#0f5c3d]/20 text-[11px] hover:bg-[#f7fbf8]">
                      <FileText className="w-3 h-3" /> View
                    </button>
                  </td>
                </tr>
              ))}</tbody>
            </table>
            {payslips.length === 0 && <div className="p-8 text-center text-[#0b1a2e]/50 text-sm">Nothing yet — click Execute to compute payslips.</div>}
          </div>
        ) : <div className="bg-white border border-black/5 rounded-2xl p-16 text-center text-[#0b1a2e]/50">Select a run.</div>}
      </div>
      {showNew && <NewRunModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </div>
  );
};

const FormField = ({ label, children }) => (
  <div><label className="block text-[10px] uppercase tracking-widest text-[#0b1a2e]/60 mb-1 font-semibold">{label}</label>{children}</div>
);
const Input = (props) => <input {...props} className={`w-full px-3 py-2 border border-slate-300 rounded text-sm ${props.className || ''}`} />;
const Select = (props) => <select {...props} className={`w-full px-3 py-2 border border-slate-300 rounded text-sm bg-white ${props.className || ''}`}>{props.children}</select>;

const NewRunModal = ({ onClose, onSaved }) => {
  const now = new Date();
  const [f, setF] = useState({ period_year: now.getFullYear(), period_month: now.getMonth() + 1, country: '' });
  const save = async () => {
    try { await payroll.createRun({ ...f, country: f.country || null }); onSaved(); }
    catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl overflow-hidden">
        <div className="px-5 py-4 bg-[#0b1a2e] text-white flex justify-between"><div className="font-medium">New payroll run</div><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Year"><Input type="number" value={f.period_year} onChange={(e) => setF({ ...f, period_year: parseInt(e.target.value, 10) })} data-testid="run-year" /></FormField>
            <FormField label="Month"><Input type="number" min={1} max={12} value={f.period_month} onChange={(e) => setF({ ...f, period_month: parseInt(e.target.value, 10) })} data-testid="run-month" /></FormField>
          </div>
          <FormField label="Country (optional)">
            <Select value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })}>
              <option value="">All countries</option>
              <option value="IN">India</option><option value="UK">United Kingdom</option>
              <option value="MY">Malaysia</option><option value="GENERIC">Other</option>
            </Select>
          </FormField>
          <button onClick={save} data-testid="save-run-btn" className="w-full mt-2 px-4 py-2 rounded-full bg-[#0f5c3d] text-white text-sm">Create</button>
        </div>
      </div>
    </div>
  );
};

const MyPayslipsTab = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { payroll.minePayslips().then((r) => setItems(r.data || [])); }, []);
  return (
    <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-[#f7fbf8]"><tr>
          <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Period</th>
          <th className="text-right p-2 text-xs uppercase text-[#0b1a2e]/60">Gross</th>
          <th className="text-right p-2 text-xs uppercase text-[#0b1a2e]/60">Net</th>
          <th></th>
        </tr></thead>
        <tbody>{items.map((p) => (
          <tr key={p.id} className="border-t border-black/5">
            <td className="p-2">{p.period_year}-{String(p.period_month).padStart(2, '0')}</td>
            <td className="p-2 text-right">{p.gross.toFixed(2)} {p.currency}</td>
            <td className="p-2 text-right font-bold text-[#0f5c3d]">{p.net.toFixed(2)}</td>
            <td className="p-2 text-right"><button onClick={() => openPayslipHtml(p.id)} className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-[#0f5c3d]/20 text-[11px]"><Download className="w-3 h-3" /> View</button></td>
          </tr>
        ))}</tbody>
      </table>
      {items.length === 0 && <div className="p-8 text-center text-[#0b1a2e]/50 text-sm">No payslips yet.</div>}
    </div>
  );
};

const LoansTab = () => {
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const load = useCallback(async () => {
    const [l, t, e] = await Promise.all([payroll.loans(), payroll.loanTypes(), payroll.employees()]);
    setItems(l.data || []); setTypes(t.data || []); setEmployees(e.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  const typeById = Object.fromEntries(types.map((t) => [t.id, t]));
  const empById = Object.fromEntries(employees.map((e) => [e.user_id, e]));
  return (
    <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
      <div className="p-3 border-b border-black/5 flex justify-between">
        <div className="text-sm">{items.length} loans</div>
        <button onClick={() => setShowNew(true)} data-testid="new-loan-btn" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#0f5c3d] text-white text-xs"><Plus className="w-3.5 h-3.5" /> New loan</button>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-[#f7fbf8]"><tr>
          <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Employee</th>
          <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Type</th>
          <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Purpose</th>
          <th className="text-right p-2 text-xs uppercase text-[#0b1a2e]/60">Principal</th>
          <th className="text-right p-2 text-xs uppercase text-[#0b1a2e]/60">EMI</th>
          <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Status</th>
        </tr></thead>
        <tbody>{items.map((l) => (
          <tr key={l.id} className="border-t border-black/5">
            <td className="p-2 text-xs">{empById[l.user_id]?.name || l.user_id}</td>
            <td className="p-2 text-xs">{typeById[l.loan_type_id]?.name || '-'}</td>
            <td className="p-2">{l.purpose || '—'}</td>
            <td className="p-2 text-right">{l.principal.toFixed(2)}</td>
            <td className="p-2 text-right">{l.emi_amount.toFixed(2)}</td>
            <td className="p-2 text-xs">{l.status}</td>
          </tr>
        ))}</tbody>
      </table>
      {items.length === 0 && <div className="p-8 text-center text-sm text-[#0b1a2e]/50">No loans issued.</div>}
      {showNew && <NewLoanModal types={types} employees={employees} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </div>
  );
};

const NewLoanModal = ({ types, employees, onClose, onSaved }) => {
  const [f, setF] = useState({ user_id: '', loan_type_id: '', purpose: '', principal: '', emi_amount: '' });
  const save = async () => {
    try {
      await payroll.createLoan({ ...f, principal: parseFloat(f.principal), emi_amount: parseFloat(f.emi_amount) });
      toast({ title: 'Loan created' });
      onSaved();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden">
        <div className="px-5 py-4 bg-[#0b1a2e] text-white flex justify-between"><div className="font-medium">New loan</div><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-5 space-y-3">
          <FormField label="Employee">
            <Select value={f.user_id} onChange={(e) => setF({ ...f, user_id: e.target.value })} data-testid="loan-employee">
              <option value="">Select</option>{employees.map((e) => <option key={e.user_id} value={e.user_id}>{e.name || e.email}</option>)}
            </Select>
          </FormField>
          <FormField label="Loan type (from master)">
            <Select value={f.loan_type_id} onChange={(e) => setF({ ...f, loan_type_id: e.target.value })} data-testid="loan-type">
              <option value="">Select</option>{types.filter((t) => t.active).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </Select>
          </FormField>
          <FormField label="Purpose (optional)"><Input value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} /></FormField>
          <div className="grid grid-cols-2 gap-2">
            <FormField label="Principal"><Input type="number" value={f.principal} onChange={(e) => setF({ ...f, principal: e.target.value })} data-testid="loan-principal" /></FormField>
            <FormField label="EMI amount"><Input type="number" value={f.emi_amount} onChange={(e) => setF({ ...f, emi_amount: e.target.value })} data-testid="loan-emi" /></FormField>
          </div>
          <button onClick={save} data-testid="loan-save-btn" className="w-full mt-2 px-4 py-2 rounded-full bg-[#0f5c3d] text-white text-sm font-semibold">Create</button>
        </div>
      </div>
    </div>
  );
};

const ReimbTab = () => {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const load = useCallback(async () => {
    const [r, c] = await Promise.all([payroll.reimbs(), payroll.reimbCats()]);
    setItems(r.data || []); setCats(c.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  const decide = async (r, decision) => { await payroll.decideReimb(r.id, { decision }); load(); };
  const catById = Object.fromEntries(cats.map((c) => [c.id, c]));
  return (
    <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
      <div className="p-3 border-b border-black/5 flex justify-between">
        <div className="text-sm">{items.length} claims</div>
        <button onClick={() => setShowNew(true)} data-testid="new-reimb-btn" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#0f5c3d] text-white text-xs"><Plus className="w-3.5 h-3.5" /> New claim</button>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-[#f7fbf8]"><tr>
          <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">User</th>
          <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Category</th>
          <th className="text-right p-2 text-xs uppercase text-[#0b1a2e]/60">Amount</th>
          <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Date</th>
          <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Status</th>
          <th></th>
        </tr></thead>
        <tbody>{items.map((r) => (
          <tr key={r.id} className="border-t border-black/5">
            <td className="p-2 text-xs">{r.user_name || r.user_id}</td>
            <td className="p-2">{catById[r.category_id]?.name || r.category}</td>
            <td className="p-2 text-right">{r.amount.toFixed(2)}</td>
            <td className="p-2 text-xs">{r.expense_date}</td>
            <td className="p-2"><span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${r.status === 'pending' ? 'bg-amber-100 text-amber-800' : r.status === 'approved' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>{r.status}</span></td>
            <td className="p-2 text-right">
              {r.status === 'pending' && (<div className="inline-flex gap-1">
                <button onClick={() => decide(r, 'approved')} className="px-2 py-0.5 rounded bg-emerald-600 text-white text-[10px]">Approve</button>
                <button onClick={() => decide(r, 'rejected')} className="px-2 py-0.5 rounded bg-red-600 text-white text-[10px]">Reject</button>
              </div>)}
            </td>
          </tr>
        ))}</tbody>
      </table>
      {items.length === 0 && <div className="p-8 text-center text-[#0b1a2e]/50 text-sm">No reimbursement claims.</div>}
      {showNew && <NewReimbModal cats={cats} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </div>
  );
};

const NewReimbModal = ({ cats, onClose, onSaved }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ category_id: '', category: '', amount: '', expense_date: today, description: '' });
  const save = async () => {
    try {
      const cat = cats.find((c) => c.id === f.category_id);
      await payroll.createReimb({ ...f, category: cat?.name || f.category, amount: parseFloat(f.amount) });
      toast({ title: 'Claim submitted' });
      onSaved();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden">
        <div className="px-5 py-4 bg-[#0b1a2e] text-white flex justify-between"><div className="font-medium">New reimbursement claim</div><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-5 space-y-3">
          <FormField label="Category (from master)">
            <Select value={f.category_id} onChange={(e) => setF({ ...f, category_id: e.target.value })} data-testid="reimb-category">
              <option value="">Select</option>{cats.filter((c) => c.active).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </FormField>
          <div className="grid grid-cols-2 gap-2">
            <FormField label="Amount"><Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="reimb-amount" /></FormField>
            <FormField label="Date"><Input type="date" value={f.expense_date} onChange={(e) => setF({ ...f, expense_date: e.target.value })} /></FormField>
          </div>
          <FormField label="Description"><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></FormField>
          <button onClick={save} data-testid="reimb-save-btn" className="w-full mt-2 px-4 py-2 rounded-full bg-[#0f5c3d] text-white text-sm font-semibold">Submit</button>
        </div>
      </div>
    </div>
  );
};

export default PayrollPage;
